import DocsFileIcon from '@assets/icons/docs-file.svg?react';
import PDFFileIcon from '@assets/icons/pdf-file.svg?react';
import { DropboxFileItem } from '@common/types/dropbox';
import { VictronImage } from '@components/display/images';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Divider, IconButton, IconButtonProps, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { formatBytes } from '@utils/file';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

type MediaItemCardProps = DropboxFileItem & {
    loading?: boolean;
    baseUrl?: string;
    onClick?: () => void;
    downloadProps?: Omit<IconButtonProps, 'children'>;
    favoriteProps?: Omit<IconButtonProps, 'children'>;
    thumbnailProps?: React.ImgHTMLAttributes<HTMLImageElement> & { animated?: boolean };
};

export const MediaItemCard = ({
    loading,
    file_type,
    thumbnail,
    thumbnailProps: thumbnailPropsRaw,
    name,
    size,
    path,
    baseUrl,
    is_favorite,
    onClick,
    downloadProps,
    favoriteProps,
}: MediaItemCardProps) => {
    const intl = useIntl();
    const { animated: animatedThumbnail = true, ...thumbnailProps } = thumbnailPropsRaw ?? {};

    return (
        <Tooltip title={name} placement="top">
            <Stack
                p="14px"
                direction="column"
                gap="20px"
                bgcolor="background.paper"
                border={1}
                borderColor="rgba(0,0,0,0.16)"
                borderRadius="4px"
                className={clsx('group', {
                    'cursor-pointer': !loading && !!onClick,
                })}
                component={file_type === 'folder' ? Link : 'div'}
                to={file_type === 'folder' && path ? `${baseUrl}${path.toLowerCase()}` : undefined}
                position="relative"
                onClick={onClick}
            >
                {!loading && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap="5px"
                        position="absolute"
                        top="20px"
                        right="22px"
                        zIndex={50}
                    >
                        <IconButton
                            size="small"
                            color="white"
                            className="hidden group-hover:flex shadow"
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                            classes={{
                                loading: 'bg-white',
                            }}
                            {...downloadProps}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                downloadProps?.onClick?.(e);
                            }}
                        >
                            <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        {favoriteProps && (
                            <IconButton
                                size="small"
                                color="neutral-500"
                                className={clsx('group-hover:flex shadow', { hidden: !is_favorite })}
                                sx={{ bgcolor: 'white.main', '&:hover': { bgcolor: 'neutral-300.main' } }}
                                {...favoriteProps}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    favoriteProps?.onClick?.(e);
                                }}
                            >
                                {is_favorite ? (
                                    <StarIcon sx={{ fontSize: 16 }} color="primary-300" />
                                ) : (
                                    <StarBorderIcon sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        )}
                    </Stack>
                )}
                {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={195} />
                ) : ['folder', 'file'].includes(file_type ?? '') && !thumbnail ? (
                    <Box
                        height={195}
                        bgcolor={(theme) => theme.palette['neutral-100'].main}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {file_type === 'folder' ? (
                            <FolderOutlinedIcon
                                sx={{ fontSize: 105 }}
                                color="primary"
                                className="transition-transform duration-300 ease-out-quad group-hover:scale-105"
                            />
                        ) : name?.endsWith('.doc') || name?.endsWith('.docx') ? (
                            <DocsFileIcon width={105} height={105} />
                        ) : name?.endsWith('.pdf') ? (
                            <PDFFileIcon width={105} height={105} />
                        ) : (
                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 105 }} color="primary" />
                        )}
                    </Box>
                ) : (
                    <VictronImage
                        src={thumbnail}
                        decoding="async"
                        alt={intl.formatMessage(
                            { id: 'media.thumbnail', defaultMessage: 'Media Item Thumbnail of {name}' },
                            { name }
                        )}
                        {...thumbnailProps}
                        className={clsx(
                            'h-[195px] w-full object-cover',
                            {
                                'transition-transform duration-300 ease-out-quad group-hover:scale-105':
                                    animatedThumbnail,
                            },
                            thumbnailProps?.className
                        )}
                    />
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center" height="60px" gap="10px">
                    <Box minWidth={0} flex={1}>
                        <Typography
                            fontFamily="museo-sans"
                            fontSize="14px"
                            letterSpacing={0}
                            className="line-clamp-2 text-ellipsis"
                        >
                            {loading ? <Skeleton variant="text" /> : name?.replace(/\.[^/.]+$/, '')}
                        </Typography>
                        <Typography fontFamily="museo-sans" fontSize="10px" color="neutral-800">
                            {loading ? (
                                <Skeleton variant="text" width="40%" />
                            ) : size != null ? (
                                formatBytes(size)
                            ) : null}
                        </Typography>
                    </Box>
                    {loading ? (
                        <Skeleton variant="rounded" width="20%" height={20} />
                    ) : (
                        <Box border={2} borderRadius="7px" p="3px">
                            <Typography fontSize="12px" fontWeight={500} lineHeight="14px">
                                {file_type === 'folder'
                                    ? 'FOLDER'
                                    : `.${path?.split('.').pop()?.toUpperCase() ?? file_type?.toUpperCase()}`}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Tooltip>
    );
};
